import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "react-toastify";

import { isRegurnDateValid } from "../../../helpers";
import { addBusName, addError } from "../../../redux/action/busAction";

import BoardingAndDropping from "../BoardingAndDropping/index.jsx";
import BusSeatMap from "../BusSeatMap/index.jsx";

import {
  BookingBtn,
  BusSeatWrapper,
} from "./BustSeat.styles.js";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const API_BASE_URL = String(
  process.env.REACT_APP_API_MODULE_DOMAIN || ""
).replace(/\/+$/, "");

const isStaffSeat = (seat) =>
  String(seat?.seat_no || "")
    .trim()
    .toUpperCase()
    .includes("STAFF");

const isVipSeat = (seat) =>
  String(seat?.seat_no || "")
    .trim()
    .toUpperCase()
    .startsWith("VIP");

const isDriverSeat = (seat) =>
  String(seat?.column_element || "")
    .trim()
    .toLowerCase() === "driver";

const isBookableSeat = (seat) =>
  Boolean(String(seat?.seat_no || "").trim()) &&
  !isStaffSeat(seat) &&
  !isDriverSeat(seat);

const getSeatPrice = (seat, tripData) => {
  if (!isBookableSeat(seat)) return null;

  const rawFare = isVipSeat(seat)
    ? tripData?.vip_fare ?? tripData?.special_fair
    : tripData?.normal_fare ?? tripData?.adult_fair;

  if (
    rawFare === null ||
    rawFare === undefined ||
    String(rawFare).trim() === ""
  ) {
    return null;
  }

  const fare = Number(rawFare);

  return Number.isFinite(fare) && fare >= 0 ? fare : null;
};

const readLocalStorageJSON = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
};

const normalizeJourneyDate = (value) => {
  if (!value || !String(value).trim()) {
    return null;
  }

  const parsed = moment(
    String(value).trim(),
    ["YYYY-MM-DD", "YYYY-M-D"],
    true
  );

  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const createBookingToken = () => {
  if (
    typeof window === "undefined" ||
    !window.crypto ||
    typeof window.crypto.getRandomValues !== "function"
  ) {
    throw new Error(
      "Secure booking is unavailable in this browser. Please use an updated browser over HTTPS."
    );
  }

  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);

  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const getBookingToken = () => {
  let token = localStorage.getItem("ticket_token");

  if (!token || !/^[a-f0-9]{48}$/i.test(token)) {
    token = createBookingToken();
    localStorage.setItem("ticket_token", token);
  }

  return token;
};

const readApiResponse = async (response, defaultMessage) => {
  const responseText = await response.text();

  let result;

  try {
    result = responseText ? JSON.parse(responseText) : {};
  } catch {
    console.error("Invalid API response:", responseText);

    throw new Error(
      "The server returned an invalid response. Please try again."
    );
  }

  if (!response.ok) {
    throw new Error(
      result?.message ||
        result?.error ||
        `${defaultMessage} (HTTP ${response.status})`
    );
  }

  return result;
};

const getPositiveNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) && number > 0 ? number : 0;
};

const getNonNegativeNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) && number >= 0 ? number : 0;
};

/* -------------------------------------------------------------------------- */
/* Bus Seat Component                                                         */
/* -------------------------------------------------------------------------- */

const BusSeat = ({ tripData, setFirstJourneyDateCheck }) => {
  const { webSettingData, languageData } = useSelector(
    (state) => state.busLists
  );

  const history = useHistory();
  const dispatch = useDispatch();

  const [boardingInformation, setBoardingInformation] = useState({
    time: "",
    detail: "",
    standName: "",
    stand_id: "",
  });

  const [droppingInformation, setDroppingInformation] = useState({
    time: "",
    detail: "",
    standName: "",
    stand_id: "",
  });

  const [luggageData, setLuggageData] = useState(null);

  const [newLuggageData, setNewLuggageData] = useState({
    free_luggage_pcs: "",
    free_luggage_kg: "",
    paid_max_luggage_pcs: "",
    paid_max_luggage_kg: "",
  });

  const [specialLuggageName, setSpecialLuggageName] = useState("");

  const [selectedSeatNumbers, setSelectedSeatNumbers] = useState([]);

  const [isLoading, setLoading] = useState(false);

  // Read the current search information when the component initializes.
  const [searchData, setSearchData] = useState(() =>
    readLocalStorageJSON("searchInfo")
  );

  const [journeyInfo, setJourneyInfo] = useState(() =>
    readLocalStorageJSON("journeyInfo")
  );

  /* ------------------------------------------------------------------------ */
  /* Selected seats and fares                                                 */
  /* ------------------------------------------------------------------------ */

  const bookableSelectedSeats = useMemo(() => {
    const seen = new Set();

    return selectedSeatNumbers.filter((seat) => {
      if (!isBookableSeat(seat)) return false;

      const seatNumber = String(seat.seat_no).trim().toUpperCase();

      if (seen.has(seatNumber)) return false;

      seen.add(seatNumber);

      return true;
    });
  }, [selectedSeatNumbers]);

  const totalSeats = bookableSelectedSeats.length;

  const adultSelectSeat = bookableSelectedSeats.filter(
    (seat) => !isVipSeat(seat)
  ).length;

  const specialSelectSeat = bookableSelectedSeats.filter(
    isVipSeat
  ).length;

  const childrenSelectSeat = 0;

  const seatPrices = useMemo(
    () =>
      bookableSelectedSeats.map((seat) =>
        getSeatPrice(seat, tripData)
      ),
    [bookableSelectedSeats, tripData]
  );

  const hasInvalidFare = seatPrices.some(
    (price) => price === null
  );

  const ticketPrice = useMemo(
    () =>
      seatPrices.reduce(
        (sum, price) => sum + (price ?? 0),
        0
      ),
    [seatPrices]
  );

  /* ------------------------------------------------------------------------ */
  /* Luggage calculations                                                     */
  /* ------------------------------------------------------------------------ */

  const paidLuggagePcs = getNonNegativeNumber(
    newLuggageData?.paid_max_luggage_pcs
  );

  const specialLuggageKg = getNonNegativeNumber(
    newLuggageData?.paid_max_luggage_kg
  );

  const paidLuggageUnitPrice = getNonNegativeNumber(
    luggageData?.price_pcs
  );

  const specialLuggageUnitPrice = getNonNegativeNumber(
    luggageData?.special_price_pcs
  );

  const paidLuggageCost =
    paidLuggageUnitPrice * paidLuggagePcs;

  const specialLuggageCost =
    specialLuggageUnitPrice * specialLuggageKg;

  const totalLuggageCost =
    paidLuggageCost + specialLuggageCost;

  const grandTotal = ticketPrice + totalLuggageCost;

  /* ------------------------------------------------------------------------ */
  /* Language and child component data                                        */
  /* ------------------------------------------------------------------------ */

  const languageText = (key, fallback) =>
    languageData?.[key]?.[webSettingData?.language] || fallback;

  const busDetails = {
    totalSelectSeat: totalSeats,
    setTotalSelectSeat: () => {},

    childrenSelectSeat,
    setChildrenSelectSeat: () => {},

    adultSelectSeat,
    setAdultSelectSeat: () => {},

    specialSelectSeat,
    setSpecialSelectSeat: () => {},

    setLuggageData,
  };

  /* ------------------------------------------------------------------------ */
  /* Return trip / checkout handling                                          */
  /* ------------------------------------------------------------------------ */

  const saveSeatBookingInfo = async (bookingInfo) => {
    const normalizedJourneyDate = normalizeJourneyDate(
      searchData?.journeydate
    );

    const normalizedReturnDate = normalizeJourneyDate(
      searchData?.returnDate
    );

    if (!normalizedJourneyDate) {
      throw new Error(
        "Journey date is invalid. Please search for the trip again."
      );
    }

    const shouldLoadReturnTrip =
      Boolean(normalizedReturnDate) &&
      (
        (
          !isRegurnDateValid(searchData?.returnDate) &&
          moment(
            normalizedJourneyDate,
            "YYYY-MM-DD",
            true
          ).isBefore(
            moment(normalizedReturnDate, "YYYY-MM-DD", true)
          )
        ) ||
        (
          moment(
            normalizedJourneyDate,
            "YYYY-MM-DD",
            true
          ).isSame(
            moment(normalizedReturnDate, "YYYY-MM-DD", true)
          ) &&
          !journeyInfo
        )
      );

    if (shouldLoadReturnTrip) {
      if (!API_BASE_URL) {
        throw new Error("API server address is not configured.");
      }

      const formData = new FormData();

      formData.append(
        "pick_location_id",
        searchData?.dropLocation
      );

      formData.append(
        "drop_location_id",
        searchData?.pickLocation
      );

      formData.append(
        "journeydate",
        normalizedReturnDate
      );

      const response = await fetch(
        `${API_BASE_URL}/triplist`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await readApiResponse(
        response,
        "Unable to load the return trip."
      );

      if (result?.status !== "success" || !result?.data) {
        dispatch(addError(result));

        throw new Error(
          result?.message || "Unable to load the return trip."
        );
      }

      dispatch(addBusName(result.data));

      const nextSearchInfo = {
        dropLocation: searchData.dropLocation,
        journeydate: normalizedReturnDate,
        pickLocation: searchData.pickLocation,
        returnDate: normalizedJourneyDate,
      };

      const returnFirstJourneyInfo = {
        dropLocation: searchData.dropLocation,
        pickLocation: searchData.pickLocation,
        journeydate: normalizedJourneyDate,
        returnDate: normalizedReturnDate,
      };

      const nextJourneyInfo = {
        ...bookingInfo,
        journeydate: normalizedJourneyDate,
        returnDate: normalizedReturnDate,
        isRoundTrip: true,
      };

      localStorage.setItem(
        "searchInfo",
        JSON.stringify(nextSearchInfo)
      );

      localStorage.setItem(
        "returnFirstJourneyInfo",
        JSON.stringify(returnFirstJourneyInfo)
      );

      localStorage.setItem(
        "journeyInfo",
        JSON.stringify(nextJourneyInfo)
      );

      setSearchData(nextSearchInfo);
      setJourneyInfo(nextJourneyInfo);

      // The existing return-trip flow continues from the updated trip list.
      return;
    }

    const oneWayBookingInfo = {
      ...bookingInfo,
      journeydate: normalizedJourneyDate,
      isRoundTrip: false,
    };

    localStorage.setItem(
      "bookingInfo",
      JSON.stringify(oneWayBookingInfo)
    );

    history.push("/checkout");
  };

  /* ------------------------------------------------------------------------ */
  /* Booking validation and seat availability                                 */
  /* ------------------------------------------------------------------------ */

  const handleBooking = async () => {
    if (isLoading) return;

    setFirstJourneyDateCheck?.(true);

    if (!API_BASE_URL) {
      toast.error("API server address is not configured.");
      return;
    }

    if (totalSeats === 0) {
      toast.error("Please select a seat first.");
      return;
    }

    const maxTickets = Math.max(
      1,
      Number(webSettingData?.max_ticket) || 4
    );

    if (totalSeats > maxTickets) {
      toast.error(
        `You may book a maximum of ${maxTickets} seats at a time.`
      );
      return;
    }

    if (hasInvalidFare) {
      toast.error(
        "One or more seat fares are invalid. Please refresh the trip and try again."
      );
      return;
    }

    if (!boardingInformation?.stand_id) {
      toast.error("Please select a boarding point.");
      return;
    }

    if (!droppingInformation?.stand_id) {
      toast.error("Please select a dropping point.");
      return;
    }

    if (
      String(boardingInformation.stand_id) ===
      String(droppingInformation.stand_id)
    ) {
      toast.error(
        "Boarding and dropping points must be different."
      );
      return;
    }

    const normalizedJourneyDate = normalizeJourneyDate(
      searchData?.journeydate
    );

    if (!normalizedJourneyDate) {
      toast.error(
        "Invalid or missing journey date. Please search for the trip again."
      );
      return;
    }

    if (!tripData?.subtripId || !tripData?.trip_id) {
      toast.error(
        "Trip information is missing. Please select the trip again."
      );
      return;
    }

    const seatNames = bookableSelectedSeats
      .map((seat) => String(seat.seat_no).trim())
      .filter(Boolean);

    if (
      seatNames.length !== totalSeats ||
      new Set(seatNames.map((seat) => seat.toUpperCase())).size !==
        totalSeats
    ) {
      toast.error(
        "Your selected seats are invalid. Please select your seats again."
      );
      return;
    }

    setLoading(true);

    try {
      // Reuse the token for this booking attempt.
      // The backend must enforce token uniqueness and idempotency.
      const ticketToken = getBookingToken();

      const formData = new FormData();

      formData.append("subtrip_id", String(tripData.subtripId));

      formData.append("ticket_token", ticketToken);

      formData.append("seat_names", seatNames.join(","));

      formData.append(
        "journey_date",
        normalizedJourneyDate
      );

      const checkSeatsResponse = await fetch(
        `${API_BASE_URL}/tickets/checkseats`,
        {
          method: "POST",
          body: formData,
        }
      );

      // Read the response body even for HTTP error responses.
      const checkSeatsResult = await readApiResponse(
        checkSeatsResponse,
        "Unable to verify seat availability."
      );

      if (checkSeatsResult?.status !== "success") {
        throw new Error(
          checkSeatsResult?.message ||
            "One or more seats are no longer available. Please select again."
        );
      }

      const bookingInfo = {
        trip_id: tripData.trip_id,
        subtripId: tripData.subtripId,

        journeydate: normalizedJourneyDate,

        pickstand: boardingInformation.stand_id,
        dropstand: droppingInformation.stand_id,

        totalprice: String(ticketPrice),
        grandtotal: String(grandTotal),

        aseat: String(adultSelectSeat),
        cseat: String(childrenSelectSeat),
        spseat: String(specialSelectSeat),

        vehicle_id: tripData.vehicle_id,

        seatnumbers: seatNames.join(","),

        totalseat: totalSeats,

        luggageInfo: {
          paid_max_luggage_pcs: paidLuggagePcs,
          special_max_luggage_pcs: specialLuggageKg,

          luggageCost: totalLuggageCost,

          price_pcs: paidLuggageCost,

          special_price_pcs: specialLuggageUnitPrice,
        },

        specialLuggage:
          !specialLuggageName ||
          specialLuggageName === "undefined"
            ? ""
            : specialLuggageName,

        ticket_token: ticketToken,
      };

      await saveSeatBookingInfo(bookingInfo);
    } catch (error) {
      console.error("Booking validation failed:", error);

      toast.error(
        error?.message ||
          "Unable to continue with your booking. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Load luggage settings                                                    */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    let cancelled = false;

    const controller = new AbortController();

    const getLuggage = async () => {
      if (!tripData?.subtripId) {
        setLuggageData(null);
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/tickets/luggage-setttings/${encodeURIComponent(
            tripData.subtripId
          )}`,
          {
            method: "GET",
            signal: controller.signal,
          }
        );

        const result = await readApiResponse(
          response,
          "Unable to load luggage settings."
        );

        if (cancelled) return;

        const settings =
          result?.luggageSettings ??
          result?.data?.luggageSettings ??
          result?.data ??
          null;

        if (result?.status === "success" && settings) {
          setLuggageData(settings);
        } else {
          setLuggageData(null);
        }
      } catch (error) {
        if (error?.name === "AbortError") return;

        if (!cancelled) {
          console.error(
            "Unable to fetch luggage settings:",
            error
          );

          setLuggageData(null);
        }
      }
    };

    getLuggage();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [tripData?.subtripId]);

  /* ------------------------------------------------------------------------ */
  /* Right booking panel                                                      */
  /* ------------------------------------------------------------------------ */

  const rightPanel = (
    <div className="bus-seat-booking-panel">
      <section className="bus-seat-points-section">
        <BoardingAndDropping
          id={tripData?.id}
          tripId={tripData?.trip_id}
          setBoardingInfromation={setBoardingInformation}
          setDroppingInformation={setDroppingInformation}
        />
      </section>

      <BookingBtn
        onClick={handleBooking}
        disabled={isLoading}
        btnbgcolor={webSettingData?.buttoncolor}
        btnbghvcolor={webSettingData?.buttoncolorhover}
        btntextcolor={webSettingData?.buttontextcolor}
      >
        {isLoading
          ? "Please wait..."
          : languageText(
              "booking_page_Proccess_to_book_btn",
              "Proceed With Booking"
            )}
      </BookingBtn>
    </div>
  );

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <BusSeatWrapper>
      <BusSeatMap
        tripData={tripData}
        busDetails={busDetails}
        selectedSeatNumbers={selectedSeatNumbers}
        setSelectedSeatNumbers={setSelectedSeatNumbers}
        luggageSettings={luggageData}
        setNewLuggageData={setNewLuggageData}
        newLuggageData={newLuggageData}
        specialLuggageName={specialLuggageName}
        setSpecialLuggageName={setSpecialLuggageName}
        rightPanel={rightPanel}
      />
    </BusSeatWrapper>
  );
};

export default BusSeat;
